import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useImperativeHandle,
	useRef
} from "react";
import { GridItem } from "./SharePointOverView";
import { DocumentListItem } from "../DocumentListItem";
import {
	SelectableItemOptions,
	useUserTimezone
} from "client/Hooks";
import { DocumentItemViewModel } from "client/Api/Documents/SharePoint/common";
import {
	DateString,
	ListItem,
	Space,
	ScrollableVirtualList,
	ScrollableVirtualListHandle,
	PipeSeparator
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { getDisplayFriendlySize } from "client_legacy/Documents/fileExtensions";
import { isNotNil } from "client/Misc/utils";
import { SharePointDocumentAction, SharePointDocumentActionContext } from "./actions";
import { TimeZone } from "client/DateAndTime/timezones";
import { DocumentNoResult } from "../DocumentNoResult";

export type SharePointListViewHandle = {
	resetScrollPosition: () => void;
};

export type SharePointListViewProps = {
	isBusy: boolean;
	hasMoreItems: boolean;
	onLoadMoreItems: () => void;
	items: readonly GridItem[];
	selectionFor: (item: GridItem) => SelectableItemOptions;
	isInSelectMode: boolean;
	onEnableSelectMode(): void;
	onOpenItem: (item: DocumentItemViewModel) => void;
	selectionActions: readonly SharePointDocumentAction[];
	actionContext: SharePointDocumentActionContext;
	hasSearch: boolean;
	loadMoreIndicatorVersion: number;
};

export const SharePointListView = forwardRef(
	(
		props: SharePointListViewProps,
		ref: Ref<SharePointListViewHandle>
	): ReactElement => {
		const {
			isBusy,
			hasMoreItems,
			onLoadMoreItems,
			items,
			selectionFor,
			isInSelectMode,
			onEnableSelectMode,
			onOpenItem,
			selectionActions,
			actionContext,
			hasSearch,
			loadMoreIndicatorVersion
		} = props;

		const userTimezone: TimeZone = useUserTimezone();
		const listHandle: RefObject<ScrollableVirtualListHandle<GridItem> | null> = useRef<ScrollableVirtualListHandle<GridItem>>(null);

		useImperativeHandle(
			ref,
			() => ({
				resetScrollPosition: () => {
					listHandle.current
						?.scrollToTop();
				}
			}),
			[]
		);

		if (!isBusy && items.length === 0) {
			return (
				<DocumentNoResult
					hasSearch={hasSearch}
				/>
			);
		}

		return (
			<ScrollableVirtualList
				items={items}
				estimatedHeightOfAnItem={64}
				itemKey={getUniqueKey}
				ref={listHandle}
				renderAfterItems={
					<>
						<ScrollableVirtualList.LoadMoreIndicator
							key={loadMoreIndicatorVersion}
							isLoadingData={isBusy}
							hasMoreItems={hasMoreItems}
							onRequestMoreItems={onLoadMoreItems}
						/>
						<ScrollableVirtualList.MobilePlusMenuSpacing />
					</>
				}
				trackingId="sharepoint-list-view"
			>
				{(item: GridItem): ReactElement => (
					<DocumentListItem
						name={item.name}
						additionalInformation={<AdditionalInformation item={item} timezone={userTimezone} />}
						documentKind={item.model.itemType}
						extension={item.extension}
						isBookmarked={Boolean(item.model.bookmarkId)}
						isInfected={item.isInfected ?? false}
						isInSelectMode={isInSelectMode}
						selectable={selectionFor(item)}
						onEnableSelectMode={onEnableSelectMode}
						onOpenItem={() => onOpenItem(item.model)}
						actions={selectionActions}
						actionContext={{ ...actionContext, selection: [item.model] }}
					/>
				)}
			</ScrollableVirtualList>
		);
	}
);
SharePointListView.displayName = "SharePointListView";

function getUniqueKey(item: GridItem): string {
	return item.uniqueKey;
}

type AdditionalInformationProps = {
	item: GridItem;
	timezone: TimeZone;
};

export const AdditionalInformation = (props: AdditionalInformationProps): ReactElement => {
	const {
		item,
		timezone
	} = props;

	return (
		<ListItem.Row>
			<ListItem.SubTitleIcon kind="pen" />
			<DateString
				value={item.modifiedOn}
				format="RelativeTimestamp"
				timezone={timezone}
			/>
			<Space />
			{localize("_(Edited)By")}
			<Space />
			{item.modifiedBy}
			{isNotNil(item.size) && (
				<>
					<PipeSeparator />
					{getDisplayFriendlySize(item.size)}
				</>
			)}
		</ListItem.Row>
	);
};
AdditionalInformation.displayName = "AdditionalInformation";
